import { AssessmentDefinition, CalculationType, InputType, SideType } from '../models/assessment-definition.model';

export const BBTDefinition: AssessmentDefinition = {
  id: 'bbt',
  version: '1.0',
  name: 'Box and Block Test',
  shortName: 'BBT',
  description:
    'Der Box and Block Test misst die unilaterale grobe manuelle Geschicklichkeit und ist ein zuverlässiges Instrument zur Messung der manuellen Geschicklichkeit bei verschiedenen Patientengruppen.',
  guide:
    '<p>Der Box and Block Test besteht aus einer rechteckigen Box, die durch eine Trennwand in zwei gleiche Hälften geteilt ist. In einer Hälfte befinden sich 150 Holzwürfel (2,5 cm³). Der Patient sitzt an einem Tisch mit der Box vor sich und muss innerhalb von 60 Sekunden so viele Blöcke wie möglich von einer Seite der Box zur anderen transportieren.</p><p><strong>Durchführung:</strong> Der Untersucher demonstriert die Aufgabe. Der Patient hat 15 Sekunden Übungszeit. Danach beginnt der Test mit dem Kommando "Los" und dauert genau 60 Sekunden. Die Anzahl der transportierten Blöcke wird gezählt. Wenn mehrere Blöcke gleichzeitig transportiert werden, zählt nur ein Block. Blöcke, die auf den Tisch oder Boden fallen, nachdem sie die Trennwand passiert haben, werden mitgezählt.</p>',
  groups: [
    {
      id: 'bbt-tasks',
      title: 'Box and Block Test',
      description: 'Anzahl der transportierten Blöcke in 60 Sekunden',
      tasks: [
        {
          id: 'bbt-task-1',
          title: 'Anzahl der Blöcke',
          description: 'Geben Sie die Anzahl der Blöcke ein, die innerhalb von 60 Sekunden transportiert wurden.',
          guide:
            '<p><strong>Anleitung:</strong> Zählen Sie die Anzahl der Blöcke, die der Patient innerhalb von 60 Sekunden von einer Seite der Box zur anderen transportiert hat.</p><p>Die Norm-Werte für den Box and Block Test variieren nach Alter und Geschlecht. Die Ergebnisse werden mit diesen Normwerten verglichen, um die Handfunktion des Patienten einzuschätzen.</p>',
          inputType: InputType.Number,
          minValue: 0,
          maxValue: 150,
          defaultValue: null,
          isRequired: true,
          sideType: SideType.Both,
          icfCategories: [
            { code: 'd440', description: 'Feinmotorischer Handgebrauch' },
            { code: 'b760', description: 'Funktionen der Kontrolle von Willkürbewegungen' },
            { code: 'b730', description: 'Funktionen der Muskelkraft' },
            { code: 'b710', description: 'Funktionen der Gelenkbeweglichkeit' },
          ],
        },
      ],
      icfCategories: [
        { code: 'd440', description: 'Feinmotorischer Handgebrauch' },
        { code: 'b760', description: 'Funktionen der Kontrolle von Willkürbewegungen' },
        { code: 'b730', description: 'Funktionen der Muskelkraft' },
        { code: 'b710', description: 'Funktionen der Gelenkbeweglichkeit' },
      ],
    },
  ],
  icfCategories: [
    { code: 'd440', description: 'Feinmotorischer Handgebrauch' },
    { code: 'b760', description: 'Funktionen der Kontrolle von Willkürbewegungen' },
    { code: 'b730', description: 'Funktionen der Muskelkraft' },
    { code: 'b710', description: 'Funktionen der Gelenkbeweglichkeit' },
  ],
  calculationType: CalculationType.NormValueComparison,
  normValues: [
    // Frauen
    {
      gender: 'female',
      minAge: 20,
      maxAge: 24,
      avgValue: 83.56,
      stdDeviation: 9.27,
      calculationMethod: 'standardZScore',
    },
    {
      gender: 'female',
      minAge: 25,
      maxAge: 29,
      avgValue: 81.18,
      stdDeviation: 9.09,
      calculationMethod: 'standardZScore',
    },
    {
      gender: 'female',
      minAge: 30,
      maxAge: 34,
      avgValue: 80.86,
      stdDeviation: 9.02,
      calculationMethod: 'standardZScore',
    },
    {
      gender: 'female',
      minAge: 35,
      maxAge: 39,
      avgValue: 79.93,
      stdDeviation: 8.87,
      calculationMethod: 'standardZScore',
    },
    {
      gender: 'female',
      minAge: 40,
      maxAge: 44,
      avgValue: 79.24,
      stdDeviation: 8.77,
      calculationMethod: 'standardZScore',
    },
    {
      gender: 'female',
      minAge: 45,
      maxAge: 49,
      avgValue: 76.74,
      stdDeviation: 7.7,
      calculationMethod: 'standardZScore',
    },
    {
      gender: 'female',
      minAge: 50,
      maxAge: 54,
      avgValue: 75.2,
      stdDeviation: 8.07,
      calculationMethod: 'standardZScore',
    },
    {
      gender: 'female',
      minAge: 55,
      maxAge: 59,
      avgValue: 73.1,
      stdDeviation: 7.92,
      calculationMethod: 'standardZScore',
    },
    {
      gender: 'female',
      minAge: 60,
      maxAge: 64,
      avgValue: 71.31,
      stdDeviation: 7.87,
      calculationMethod: 'standardZScore',
    },
    {
      gender: 'female',
      minAge: 65,
      maxAge: 69,
      avgValue: 68.53,
      stdDeviation: 7.36,
      calculationMethod: 'standardZScore',
    },
    {
      gender: 'female',
      minAge: 70,
      maxAge: 74,
      avgValue: 67.56,
      stdDeviation: 6.94,
      calculationMethod: 'standardZScore',
    },
    {
      gender: 'female',
      minAge: 75,
      maxAge: 150,
      avgValue: 65.33,
      stdDeviation: 7.05,
      calculationMethod: 'standardZScore',
    },

    // Männer
    {
      gender: 'male',
      minAge: 20,
      maxAge: 24,
      avgValue: 82.44,
      stdDeviation: 8.84,
      calculationMethod: 'standardZScore',
    },
    {
      gender: 'male',
      minAge: 25,
      maxAge: 29,
      avgValue: 81.91,
      stdDeviation: 9.03,
      calculationMethod: 'standardZScore',
    },
    {
      gender: 'male',
      minAge: 30,
      maxAge: 34,
      avgValue: 81.88,
      stdDeviation: 8.45,
      calculationMethod: 'standardZScore',
    },
    {
      gender: 'male',
      minAge: 35,
      maxAge: 39,
      avgValue: 79.46,
      stdDeviation: 9.18,
      calculationMethod: 'standardZScore',
    },
    {
      gender: 'male',
      minAge: 40,
      maxAge: 44,
      avgValue: 77.98,
      stdDeviation: 9.14,
      calculationMethod: 'standardZScore',
    },
    {
      gender: 'male',
      minAge: 45,
      maxAge: 49,
      avgValue: 75.64,
      stdDeviation: 9.32,
      calculationMethod: 'standardZScore',
    },
    {
      gender: 'male',
      minAge: 50,
      maxAge: 54,
      avgValue: 75.26,
      stdDeviation: 8.95,
      calculationMethod: 'standardZScore',
    },
    {
      gender: 'male',
      minAge: 55,
      maxAge: 59,
      avgValue: 71.33,
      stdDeviation: 8.36,
      calculationMethod: 'standardZScore',
    },
    {
      gender: 'male',
      minAge: 60,
      maxAge: 64,
      avgValue: 70.06,
      stdDeviation: 7.99,
      calculationMethod: 'standardZScore',
    },
    {
      gender: 'male',
      minAge: 65,
      maxAge: 69,
      avgValue: 67.84,
      stdDeviation: 7.53,
      calculationMethod: 'standardZScore',
    },
    {
      gender: 'male',
      minAge: 70,
      maxAge: 74,
      avgValue: 64.32,
      stdDeviation: 7.52,
      calculationMethod: 'standardZScore',
    },
    {
      gender: 'male',
      minAge: 75,
      maxAge: 150,
      avgValue: 61.79,
      stdDeviation: 8.26,
      calculationMethod: 'standardZScore',
    },
  ],
  showPercentFulfillment: true,
  showICFScore: true,
  requiresPatientData: true,
  sideType: SideType.Both,
};
